import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Plus } from "lucide-react";
import { formatINR } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

function LoanList({ loans }) {
    const { t } = useLang();
    if (!loans.length)
        return (
            <Card className="rounded-2xl border-slate-100 p-8 text-center text-sm text-slate-400">
                {t("no_data")}
            </Card>
        );
    return (
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
            <div className="divide-y divide-slate-100">
                {loans.map((l) => (
                    <Link
                        key={l.id}
                        to={`/loans/${l.id}`}
                        data-testid={`loan-list-row-${l.id}`}
                        className="flex items-center justify-between p-4 hover:bg-slate-50"
                    >
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <div className="font-semibold text-slate-900 truncate">
                                    {l.customer_name || "—"}
                                </div>
                                <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                    {l.loan_code}
                                </span>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 capitalize">
                                {l.loan_type} · {l.interest_rate}%/mo · {l.tenure_months} mo
                            </div>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                            <div className="num font-bold text-slate-900">
                                {formatINR(l.outstanding_principal)}
                            </div>
                            <Badge
                                className={
                                    l.status === "active"
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                                        : "bg-slate-100 text-slate-700"
                                }
                            >
                                {l.status}
                            </Badge>
                        </div>
                    </Link>
                ))}
            </div>
        </Card>
    );
}

export default function Loans() {
    const { t } = useLang();
    const [loans, setLoans] = useState([]);

    useEffect(() => {
        api.get("/loans").then((r) => setLoans(r.data));
    }, []);

    const active = loans.filter((l) => l.status === "active");
    const closed = loans.filter((l) => l.status === "closed");

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                    {t("loans")}
                </h1>
                <Link to="/loans/new">
                    <Button
                        data-testid="new-loan-btn-top"
                        className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800 tap"
                    >
                        <Plus className="h-4 w-4 mr-1.5" />
                        {t("add_loan")}
                    </Button>
                </Link>
            </div>

            <Tabs defaultValue="active">
                <TabsList className="bg-white border border-slate-100 rounded-xl">
                    <TabsTrigger value="active" data-testid="tab-active-loans">
                        {t("active")} ({active.length})
                    </TabsTrigger>
                    <TabsTrigger value="closed" data-testid="tab-closed-loans">
                        {t("closed")} ({closed.length})
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="active" className="mt-4">
                    <LoanList loans={active} />
                </TabsContent>
                <TabsContent value="closed" className="mt-4">
                    <LoanList loans={closed} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
